import type { FeatureFlag } from 'app/api/services/site-configs/types';
import type { SportsMenuPromotedMenuItem } from 'app/modules/BettingMenu/types';

import type { DictionaryData } from 'app/lang';
import type {
  RecordOfMatchEntity,
  RecordOfObjectsState,
  RecordOfSportEntity,
  SportEntityNative,
  StageEntityNative,
  TournamentEntityNative,
  UnionEntityNative,
} from 'app/lib/betting/betting-entities';
import type { SportId } from 'app/lib/betting/constants';

// --

export type LocationList = 'announcement' | 'active-stakes' | 'streams';

export type NavigationLocationState = {
  path: string;
  list: LocationList | null;
  sportId: number | null;
  tournamentUnionId: number | null;
  tournamentId: number | null;
  tournamentStageId: number | null;
  matchId: number | null;
};

export type NavigationLocationProps = {
  sportId?: number;
  tournamentUnionId?: number;
  tournamentId?: number;
  tournamentStageId?: number;
  matchId?: number;
};

// --

export enum MENU_ITEM_TYPE {
  CONSTANT = 'constant',
  SPORT = 'sport',
  UNION = 'union',
  TOURNAMENT = 'tournament',
  STAGE = 'stage',
  MATCH = 'match',
}

export interface MenuItem {
  key: string;
  type: MENU_ITEM_TYPE;
  shortcut: boolean;
  label?: string | DictionaryData;
  caption?: string;
  title?: string | DictionaryData;
  location: string;
  active: boolean;
  disabled?: boolean;
  authOnly?: boolean;
  ff?: FeatureFlag;
  dataGaValues?: Record<string, string | number>;
}

export interface ConstantMenuItem extends MenuItem {
  type: MENU_ITEM_TYPE.CONSTANT;
  activeOn: 'exact-match' | 'prefix-match' | 'betting';
  label: string | DictionaryData;
  title?: string | DictionaryData;
}

export interface SportMenuItem extends MenuItem {
  type: MENU_ITEM_TYPE.SPORT;
  priority: number;
  sportId: number;
  entity: RecordOfSportEntity;
}

export interface SportMenuItemNative extends MenuItem {
  type: MENU_ITEM_TYPE.SPORT;
  priority: number;
  sportId: SportId;
  entity: SportEntityNative;
}

export interface UnionMenuItem extends MenuItem {
  type: MENU_ITEM_TYPE.UNION;
  priority: number;
  unionId: number;
  entity: UnionEntityNative;
}

export interface TournamentMenuItem extends MenuItem {
  type: MENU_ITEM_TYPE.TOURNAMENT;
  priority: number;
  tournamentId: number;
  entity: TournamentEntityNative;
}

export interface StageMenuItem extends MenuItem {
  type: MENU_ITEM_TYPE.STAGE;
  priority: number;
  stageId: number;
  entity: StageEntityNative;
}

export interface MatchMenuItem extends MenuItem {
  type: MENU_ITEM_TYPE.MATCH;
  matchId: number;
  matchObjects: RecordOfObjectsState['byId'];
  entity: RecordOfMatchEntity;
}

export type BettingEntityMenuItem = (
  SportMenuItemNative |
  UnionMenuItem |
  TournamentMenuItem |
  StageMenuItem |
  MatchMenuItem
  );

export type AnyMenuItem = (
  ConstantMenuItem |
  BettingEntityMenuItem |
  SportsMenuPromotedMenuItem |
  SportMenuItemNative
  );
